import type {
  ForgotUserPasswordMutation,
  ForgotUserPasswordMutationVariables
} from '@aurora/shared-generated/types/graphql-types';
import type {
  ForgotUserPasswordError,
  Form
} from '@aurora/shared-generated/types/graphql-schema-types';
import { getLog } from '@aurora/shared-utils/log';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { SharedComponent } from '../../../enums';
import FormBuilder from '../../../helpers/form/FormBuilder/FormBuilder';
import { LoadingButtonVariant } from '../../common/Button/enums';
import { ToastAlertVariant, ToastVariant } from '../../common/ToastAlert/enums';
import useToasts from '../../context/ToastContext/useToasts';
import { FormActionButtonsPosition } from '../../form/enums';
import InputEditForm from '../../form/InputEditForm/InputEditForm';
import type { FormSpec } from '../../form/types';
import type ToastProps from '../../common/ToastAlert/ToastAlertProps';
import useMutationWithTracing from '../../useMutationWithTracing';
import useTranslation from '../../useTranslation';
import formSchema from './ForgotPasswordForm.form.json';
import forgotUserPasswordMutation from './ForgotUserPassword.mutation.graphql';

const log = getLog(module);

interface FormData {
  /**
   * Email input.
   */
  email: string;
}

interface Props {
  /**
   * Callback function after form is submitted successfully.
   *
   * @callback
   */
  onFormSuccess: () => void;
}

/**
 * Displays a form where the user can specify an email where to get instructions on how to restore
 * the username and password.
 *
 * @constructor
 *
 * @author Patricio Poratto
 */
const ForgotPasswordForm: React.FC<React.PropsWithChildren<Props>> = ({ onFormSuccess }) => {
  const i18n = useTranslation(SharedComponent.FORGOT_PASSWORD_FORM);
  const { formatMessage, hasMessage, loading: textLoading } = i18n;
  const [forgotUserPassword] = useMutationWithTracing<
    ForgotUserPasswordMutation,
    ForgotUserPasswordMutationVariables
  >(module, forgotUserPasswordMutation);
  const { addToast } = useToasts();
  const cx = useClassNameMapper();

  if (textLoading) {
    return null;
  }

  function renderToast(
    email: string,
    alertVariant: ToastAlertVariant,
    titleKey: string,
    messageKey: string
  ): void {
    const toastId = `reset-email-${email}`;

    const toastProps: ToastProps = {
      toastVariant: ToastVariant.FLYOUT,
      alertVariant,
      title: formatMessage(titleKey),
      message: formatMessage(messageKey),
      id: toastId
    };
    addToast(toastProps);
  }

  function getErrorMessage(error: ForgotUserPasswordError): string {
    const { __typename: type } = error;
    const key = `ForgotPasswordForm.error.${type}`;
    return hasMessage(key) ? key : 'errorMessage';
  }

  async function onSubmit(data: FormData): Promise<void> {
    const { email } = data;
    log.info('Requesting reset password instructions for email: %s', email);
    const response = await forgotUserPassword({
      variables: { createInput: { email } }
    });
    const { data: responseData, errors } = response;
    const forgotUserPasswordErrors = errors ?? responseData?.forgotUserPassword?.errors;

    if (forgotUserPasswordErrors?.length > 0) {
      log.error('Error on forgot password: %O', errors);
      forgotUserPasswordErrors.forEach(error => {
        const regError = error as unknown as ForgotUserPasswordError;
        const message = getErrorMessage(regError);
        renderToast(email, ToastAlertVariant.DANGER, 'errorTitle', message);
      });
    } else {
      renderToast(email, ToastAlertVariant.SUCCESS, 'successTitle', 'successMessage');
      onFormSuccess();
    }
  }

  const formSpec: FormSpec<FormData> = new FormBuilder<FormData>('ForgotPasswordForm', i18n, {
    schema: formSchema as Form,
    cx
  })
    .addEmailField({
      name: 'email',
      validations: {
        required: true
      },
      attributes: {
        autoComplete: 'email'
      },
      focus: true,
      defaultValue: ''
    })
    .setActionButtonsPosition(FormActionButtonsPosition.STRETCH)
    .addSubmitAction({ buttonType: LoadingButtonVariant.LOADING_BUTTON, size: 'lg' })
    .build();

  return <InputEditForm<FormData> formSpec={formSpec} onSubmit={onSubmit} />;
};

export default ForgotPasswordForm;
